import React, { useState, useEffect } from 'react';
import {
  COOKIE_TYPES,
  COOKIE_CONFIG,
  hasCookieConsent,
  saveCookieConsent,
  getCookiePreferences
} from '../utils/cookies';
import { useLanguage } from '../contexts/LanguageContext';

const CookieBanner = () => {
  const { t } = useLanguage();
  const [isVisible, setIsVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [preferences, setPreferences] = useState({});

  useEffect(() => {
    // Verificar se o consentimento já foi dado
    if (!hasCookieConsent()) {
      setIsVisible(true);
      setPreferences(getCookiePreferences());
    }
  }, []);

  const handleAcceptAll = async () => {
    const allAccepted = {
      [COOKIE_TYPES.NECESSARY]: true,
      [COOKIE_TYPES.ANALYTICS]: true,
      [COOKIE_TYPES.FUNCTIONAL]: true,
      [COOKIE_TYPES.MARKETING]: true
    };
    await saveCookieConsent(allAccepted);
    setIsVisible(false);
  };

  const handleRejectAll = async () => {
    const defaultPreferences = {
      [COOKIE_TYPES.NECESSARY]: true,
      [COOKIE_TYPES.ANALYTICS]: true,
      [COOKIE_TYPES.FUNCTIONAL]: true,
      [COOKIE_TYPES.MARKETING]: false
    };
    await saveCookieConsent(defaultPreferences);
    setIsVisible(false);
  };

  const handleCustomSave = async () => {
    await saveCookieConsent(preferences);
    setIsVisible(false);
  };

  const handlePreferenceChange = (type, checked) => {
    setPreferences(prev => ({
      ...prev,
      [type]: checked
    }));
  };

  if (!isVisible) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-gray-200 shadow-2xl">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {!showDetails ? (
          // Banner principal
          <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4">
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                {t('cookieBanner.title')}
              </h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                {t('cookieBanner.description')}
              </p>
              <button
                onClick={() => setShowDetails(true)}
                className="text-emerald-600 hover:text-emerald-700 text-sm font-medium mt-2 underline"
              >
                {t('cookieBanner.customize')}
              </button>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 lg:ml-6">
              <button
                onClick={handleRejectAll}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors duration-200"
              >
                {t('cookieBanner.default')}
              </button>
              <button
                onClick={handleAcceptAll}
                className="px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 rounded-lg transition-all duration-200 shadow-lg"
              >
                {t('cookieBanner.acceptAll')}
              </button>
            </div>
          </div>
        ) : (
          // Painel de detalhes
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                {t('cookieBanner.details.title')}
              </h3>
              <p className="text-gray-600 text-sm">
                {t('cookieBanner.details.description')}
              </p>
            </div>

            <div className="space-y-4">
              {Object.entries(COOKIE_CONFIG).map(([type, config]) => (
                <div key={type} className="flex items-start justify-between p-4 bg-gray-50 rounded-lg">
                  <div className="flex-1 mr-4">
                    <div className="flex items-center gap-2 mb-1">
                      <h4 className="font-medium text-gray-900">{t(`cookieBanner.types.${type}.title`)}</h4>
                      {config.required && (
                        <span className="px-2 py-1 text-xs font-medium bg-emerald-100 text-emerald-700 rounded-full">
                          {t('cookieManager.preferences.necessary')}
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-600 mb-1">{t(`cookieBanner.types.${type}.description`)}</p>
                    <p className="text-xs text-gray-500">{t('cookieBanner.details.duration')}: {config.duration}</p>
                  </div>

                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id={`cookie-${type}`}
                      checked={preferences[type] || false}
                      onChange={(e) => handlePreferenceChange(type, e.target.checked)}
                      disabled={config.required}
                      className="w-4 h-4 text-emerald-600 bg-gray-100 border-gray-300 rounded focus:ring-emerald-500 focus:ring-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    />
                  </div>
                </div>
              ))}
            </div>

            <div className="flex flex-col sm:flex-row gap-3 justify-end pt-4 border-t border-gray-200">
              <button
                onClick={() => setShowDetails(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors duration-200"
              >
                {t('cookieBanner.details.back')}
              </button>
              <button
                onClick={handleCustomSave}
                className="px-6 py-2 text-sm font-medium text-white bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 rounded-lg transition-all duration-200 shadow-lg"
              >
                {t('cookieBanner.details.save')}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CookieBanner;
